<script lang="ts">
	import { resendVerificationCode, verifyEmail } from '$lib/stores/user';
	import { createEventDispatcher } from 'svelte';
	// Import tick for DOM synchronization
	import { tick } from 'svelte';
	// Cleanup timer on destroy
	import { onDestroy } from 'svelte';

	// Props
	export let isOpen: boolean = false;
	export let email: string = '';
	export let onClose = () => {};
	export let onVerifySuccess = (result: any) => {};
	export let onResendCode = () => {};
	export let countdownSeconds: number = 60; // Countdown timer in seconds (default 60 seconds = 1 minute)
	// Custom verify function - if provided, will use this instead of default verifyEmail
	export let verifyFunction: ((email: string, code: string) => Promise<any>) | null = null;
	// Custom resend function - if provided, will use this instead of default resendVerificationCode
	export let resendFunction: ((email: string) => Promise<void>) | null = null;
	// If autoVerify is false, will not call verify function but just return OTP code via onVerifySuccess
	export let autoVerify: boolean = true;
	// If autoClose is false, will not close modal automatically after verification
	export let autoClose: boolean = true;
	// If showModal is false, will not show modal overlay (for inline usage)
	export let showModal: boolean = true;

	// Event dispatcher
	const dispatch = createEventDispatcher();

	// Component state
	let otpCode: string[] = ['', '', '', '', '', ''];
	let otpInputs: HTMLInputElement[] = [];
	let isLoading = false;
	let isResending = false;
	let errorMessage = '';
	let successMessage = '';
	let countdownTimer = 0; // Countdown timer for OTP expiration
	let countdownInterval: ReturnType<typeof setInterval> | null = null;
	let resendTimer = 0;
	let timerInterval: ReturnType<typeof setInterval> | null = null;
	let mouseDownInsideModal = false;

	// Computed properties
	$: timerDisplay = `${Math.floor(resendTimer / 60)}:${(resendTimer % 60).toString().padStart(2, '0')}`;
	$: countdownDisplay = `${Math.floor(countdownTimer / 60)}:${(countdownTimer % 60).toString().padStart(2, '0')}`;

	// Functions
	function handleClose() {
		if (showModal) {
			resetForm();
			onClose();
			dispatch('close');
		}
	}

	function handleBack() {
		if (showModal) {
			dispatch('back');
		} else {
			// In inline mode, just reset and call onClose
			resetForm();
			onClose();
		}
	}

	function resetForm() {
		otpCode = ['', '', '', '', '', ''];
		errorMessage = '';
		successMessage = '';
		isLoading = false;
		isResending = false;
		resendTimer = 0;
		countdownTimer = 0;
		if (timerInterval) {
			clearInterval(timerInterval);
			timerInterval = null;
		}
		if (countdownInterval) {
			clearInterval(countdownInterval);
			countdownInterval = null;
		}
	}

	function handleOverlayClick() {
		if (!mouseDownInsideModal) {
			handleClose();
		}
	}

	function handleMouseDown() {
		mouseDownInsideModal = true;
		setTimeout(() => {
			mouseDownInsideModal = false;
		}, 100);
	}

	function focusNextInput(index: number) {
		if (index < otpInputs.length - 1 && otpInputs[index + 1]) {
			otpInputs[index + 1].focus();
		}
	}

	function focusPreviousInput(index: number) {
		if (index > 0 && otpInputs[index - 1]) {
			otpInputs[index - 1].focus();
		}
	}

	async function handleOtpInput(index: number, event: Event) {
		const target = event.target as HTMLInputElement;
		const value = target.value;

		// Only allow digits
		if (!/^\d*$/.test(value)) {
			target.value = otpCode[index];
			return;
		}

		// Handle single digit input
		if (value.length <= 1) {
			otpCode[index] = value;
			if (value && index < 5) {
				await tick();
				focusNextInput(index);
			}
		}

		// Clear any previous errors
		if (errorMessage) {
			errorMessage = '';
		}

		// Auto-submit when all digits are filled
		if (otpCode.every((digit) => digit !== '') && otpCode.join('').length === 6) {
			await tick();
			await handleVerify();
		}
	}

	async function handleOtpKeydown(index: number, event: KeyboardEvent) {
		const target = event.target as HTMLInputElement;

		if (event.key === 'Backspace' || event.key === 'Delete') {
			if (!otpCode[index] && index > 0) {
				// If current field is empty, move to previous field and clear it
				focusPreviousInput(index);
				otpCode[index - 1] = '';
			} else {
				// Clear current field
				otpCode[index] = '';
			}
		} else if (event.key === 'ArrowLeft' && index > 0) {
			event.preventDefault();
			focusPreviousInput(index);
		} else if (event.key === 'ArrowRight' && index < 5) {
			event.preventDefault();
			focusNextInput(index);
		} else if (event.key === 'Enter') {
			event.preventDefault();
			await handleVerify();
		}
	}

	async function handleOtpPaste(event: ClipboardEvent) {
		event.preventDefault();
		const pastedData = event.clipboardData?.getData('text') || '';
		const digits = pastedData.replace(/\D/g, '').slice(0, 6);

		if (digits.length > 0) {
			for (let i = 0; i < 6; i++) {
				otpCode[i] = digits[i] || '';
			}

			// Focus the next empty field or the last field
			const nextEmptyIndex = otpCode.findIndex((digit) => digit === '');
			const targetIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;

			await tick();
			if (otpInputs[targetIndex]) {
				otpInputs[targetIndex].focus();
			}

			// Auto-submit if all digits are filled
			if (otpCode.every((digit) => digit !== '') && otpCode.join('').length === 6) {
				await tick();
				await handleVerify();
			}
		}
	}

	async function handleVerify() {
		const code = otpCode.join('');

		if (code.length !== 6) {
			errorMessage = 'Please enter a 6-digit verification code';
			return;
		}

		if (!email) {
			errorMessage = 'Email not found. Please try again';
			return;
		}

		// Check if countdown timer has expired
		if (countdownTimer <= 0) {
			errorMessage = 'Verification code has expired. Please request a new code.';
			setTimeout(() => {
				handleBack();
			}, 2000);
			return;
		}

		isLoading = true;
		errorMessage = '';
		successMessage = '';

		try {
			if (autoVerify) {
				// Auto verify mode: Use custom verify function if provided, otherwise use default verifyEmail
				const verifyFn = verifyFunction || verifyEmail;
				const result = await verifyFn(email, code);
				successMessage = 'Verification successful!';
				// Stop countdown timer on success
				if (countdownInterval) {
					clearInterval(countdownInterval);
					countdownInterval = null;
				}
				setTimeout(() => {
					onVerifySuccess(result);
					if (autoClose) {
						handleClose();
					}
				}, 1000);
			} else {
				// Manual mode: Verify OTP if verifyFunction is provided, otherwise return code directly
				if (verifyFunction) {
					// Verify OTP with backend first
					try {
						const result = await verifyFunction(email, code);
						successMessage = 'Verification successful!';
						// Stop countdown timer on success
						if (countdownInterval) {
							clearInterval(countdownInterval);
							countdownInterval = null;
						}
						setTimeout(() => {
							// Always return code and email along with verify result
							// This ensures the parent component can access the code
							const verifyResult =
								typeof result === 'object' && result !== null
									? { ...result, code, email }
									: { code, email, verified: result };
							onVerifySuccess(verifyResult);
							if (autoClose) {
								handleClose();
							}
						}, 1000);
					} catch (verifyError: any) {
						// Re-throw the error to be caught by outer catch block
						throw verifyError;
					}
				} else {
					// No verify function provided: just return OTP code without verifying
					successMessage = 'Code entered successfully!';
					// Stop countdown timer on success
					if (countdownInterval) {
						clearInterval(countdownInterval);
						countdownInterval = null;
					}
					setTimeout(() => {
						onVerifySuccess({ code, email });
						if (autoClose) {
							handleClose();
						}
					}, 1000);
				}
			}
		} catch (error: any) {
			console.error('Verification error:', error);
			const errorMsg =
				error.response?.data?.message ||
				error.message ||
				'Invalid verification code. Please try again.';
			errorMessage = errorMsg;
			// Clear OTP inputs on error
			otpCode = ['', '', '', '', '', ''];
			// Focus first input
			setTimeout(() => {
				if (otpInputs[0]) {
					otpInputs[0].focus();
				}
			}, 100);
		} finally {
			isLoading = false;
		}
	}

	async function handleResend() {
		if (isResending || resendTimer > 0) return;

		if (!email) {
			errorMessage = 'Email not found. Please try again';
			return;
		}

		isResending = true;
		errorMessage = '';
		successMessage = '';
		otpCode = ['', '', '', '', '', '']; // Clear OTP inputs

		try {
			// Use custom resend function if provided, otherwise use default resendVerificationCode
			const resendFn = resendFunction || resendVerificationCode;
			await resendFn(email);
			successMessage = 'Verification code has been resent';
			startResendTimer();
			startCountdownTimer(); // Restart countdown timer
			// Focus first input
			setTimeout(() => {
				if (otpInputs[0]) {
					otpInputs[0].focus();
				}
			}, 100);
		} catch (error: any) {
			console.error('Resend error:', error);
			const errorMsg =
				error.response?.data?.message ||
				error.message ||
				'Failed to resend code. Please try again.';
			errorMessage = errorMsg;
		} finally {
			isResending = false;
		}

		onResendCode();
	}

	function startResendTimer() {
		// Clear existing timer if any
		if (timerInterval) {
			clearInterval(timerInterval);
			timerInterval = null;
		}
		resendTimer = 60; // 1 minute
		timerInterval = setInterval(() => {
			resendTimer--;
			if (resendTimer <= 0) {
				clearInterval(timerInterval!);
				timerInterval = null;
			}
		}, 1000);
	}

	function startCountdownTimer() {
		// Clear existing timer if any
		if (countdownInterval) {
			clearInterval(countdownInterval);
			countdownInterval = null;
		}
		countdownTimer = countdownSeconds;
		countdownInterval = setInterval(() => {
			countdownTimer--;
			if (countdownTimer <= 0) {
				clearInterval(countdownInterval!);
				countdownInterval = null;
				// Timer expired, go back to email input
				errorMessage = 'Verification code has expired. Please request a new code.';
				setTimeout(() => {
					handleBack();
				}, 2000);
			}
		}, 1000);
	}

	// Track if modal was opened to prevent multiple timer starts
	let wasOpened = false;

	// Start countdown timer when modal opens
	$: if (isOpen && email && !wasOpened) {
		wasOpened = true;
		resetForm();
		startCountdownTimer();
	}

	// Reset wasOpened when modal closes
	$: if (!isOpen) {
		wasOpened = false;
	}

	// Cleanup timers on destroy
	onDestroy(() => {
		if (timerInterval) {
			clearInterval(timerInterval);
		}
		if (countdownInterval) {
			clearInterval(countdownInterval);
		}
	});

	// Auto-focus first input when modal opens
	$: if (isOpen && otpInputs[0] && wasOpened) {
		setTimeout(() => {
			otpInputs[0]?.focus();
		}, 100);
	}
</script>

{#if isOpen}
	{#if showModal}
		<div
			class="modal-overlay"
			on:click={handleOverlayClick}
			on:keydown={(e) => e.key === 'Escape' && handleClose()}
			role="dialog"
			aria-modal="true"
			tabindex="-1"
		>
			<!-- svelte-ignore a11y-click-events-have-key-events -->
			<div
				class="modal-container"
				on:click|stopPropagation
				on:mousedown={handleMouseDown}
				role="button"
				tabindex="0"
			>
				<div class="modal-header">
					<button class="close-button" on:click={handleClose} aria-label="Close">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
							<path d="M6 6L18 18M6 18L18 6" />
						</svg>
					</button>
				</div>

				<div class="modal-content">
					<!-- Email Icon -->
					<div class="icon-container">
						<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
							<path
								d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
							/>
							<polyline points="22,6 12,13 2,6" />
						</svg>
					</div>

					<h2 class="modal-title">Verify Your Email</h2>

					<div class="instruction">
						<p>We've sent a 6-digit verification code to</p>
						<p class="email">{email}</p>
						<p>Please enter the code below to verify your account.</p>
						{#if countdownTimer > 0}
							<p class="countdown-text">
								Code expires in: <span class="countdown-timer">{countdownDisplay}</span>
							</p>
						{:else}
							<p class="countdown-expired">Code expired</p>
						{/if}
					</div>

					<form class="verification-form" on:submit|preventDefault={handleVerify}>
						<!-- OTP Input -->
						<div class="otp-container">
							{#each otpCode as digit, index}
								<input
									type="text"
									class="otp-input"
									maxlength="1"
									inputmode="numeric"
									pattern="[0-9]"
									autocomplete="one-time-code"
									data-otp-index={index}
									bind:this={otpInputs[index]}
									bind:value={otpCode[index]}
									on:input={(e) => handleOtpInput(index, e)}
									on:keydown={(e) => handleOtpKeydown(index, e)}
									on:paste={handleOtpPaste}
									on:focus={(e) => {
										const target = e.target as HTMLInputElement;
										if (target) target.select();
									}}
									disabled={isLoading || countdownTimer <= 0}
									placeholder=""
								/>
							{/each}
						</div>

						{#if errorMessage}
							<div class="error-message">
								{errorMessage}
							</div>
						{/if}

						{#if successMessage}
							<div class="success-message">
								{successMessage}
							</div>
						{/if}

						<!-- Resend Code -->
						<div class="resend-section">
							<p>Didn't receive the code?</p>
							<button
								type="button"
								class="resend-button"
								on:click={handleResend}
								disabled={isResending || resendTimer > 0}
							>
								{#if isResending}
									<div class="spinner"></div>
									Sending...
								{:else if resendTimer > 0}
									Resend in {timerDisplay}
								{:else}
									Resend Code
								{/if}
							</button>
						</div>

						<!-- Action Buttons -->
						<div class="action-buttons">
							<button type="button" class="back-button" on:click={handleBack}>
								<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
									<path d="M19 12H5M12 19l-7-7 7-7" />
								</svg>
								Back
							</button>

							<button
								type="submit"
								class="verify-button"
								disabled={isLoading || otpCode.join('').length !== 6 || countdownTimer <= 0}
							>
								{#if isLoading}
									<div class="spinner"></div>
									Verifying...
								{:else}
									Verify
								{/if}
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	{:else}
		<!-- Inline mode - no modal overlay -->
		<div class="verification-content">
			<!-- Email Icon -->
			<div class="icon-container">
				<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
					<path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
					<polyline points="22,6 12,13 2,6" />
				</svg>
			</div>

			<h2 class="modal-title">Verify OTP Code</h2>

			<div class="instruction">
				<p>We've sent a 6-digit verification code to</p>
				<p class="email">{email}</p>
				<p>Please enter the code below to verify your account.</p>
				{#if countdownTimer > 0}
					<p class="countdown-text">
						Code expires in: <span class="countdown-timer">{countdownDisplay}</span>
					</p>
				{:else}
					<p class="countdown-expired">Code expired</p>
				{/if}
			</div>

			<form class="verification-form" on:submit|preventDefault={handleVerify}>
				<!-- OTP Input -->
				<div class="otp-container">
					{#each otpCode as digit, index}
						<input
							type="text"
							class="otp-input"
							maxlength="1"
							inputmode="numeric"
							pattern="[0-9]"
							autocomplete="one-time-code"
							data-otp-index={index}
							bind:this={otpInputs[index]}
							bind:value={otpCode[index]}
							on:input={(e) => handleOtpInput(index, e)}
							on:keydown={(e) => handleOtpKeydown(index, e)}
							on:paste={handleOtpPaste}
							on:focus={(e) => {
								const target = e.target as HTMLInputElement;
								if (target) target.select();
							}}
							disabled={isLoading || countdownTimer <= 0}
							placeholder=""
						/>
					{/each}
				</div>

				{#if errorMessage}
					<div class="error-message">
						{errorMessage}
					</div>
				{/if}

				{#if successMessage}
					<div class="success-message">
						{successMessage}
					</div>
				{/if}

				<!-- Resend Code -->
				<div class="resend-section">
					<p>Didn't receive the code?</p>
					<button
						type="button"
						class="resend-button"
						on:click={handleResend}
						disabled={isResending || resendTimer > 0}
					>
						{#if isResending}
							<div class="spinner"></div>
							Sending...
						{:else if resendTimer > 0}
							Resend in {timerDisplay}
						{:else}
							Resend Code
						{/if}
					</button>
				</div>

				<!-- Action Buttons -->
				<div class="action-buttons">
					<button type="button" class="back-button" on:click={handleBack}>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
							<path d="M19 12H5M12 19l-7-7 7-7" />
						</svg>
						Back
					</button>

					<button
						type="submit"
						class="verify-button"
						disabled={isLoading || otpCode.join('').length !== 6 || countdownTimer <= 0}
					>
						{#if isLoading}
							<div class="spinner"></div>
							Verifying...
						{:else}
							Verify
						{/if}
					</button>
				</div>
			</form>
		</div>
	{/if}
{/if}

<style scoped>
	.modal-overlay {
		position: fixed;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		background: rgba(0, 0, 0, 0.8);
		display: flex;
		align-items: center;
		justify-content: center;
		z-index: 1000;
		padding: 20px;
	}

	.modal-container {
		background: #1a1a1a;
		border-radius: 16px;
		width: 100%;
		max-width: 500px;
		max-height: 90vh;
		overflow-y: auto;
		box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
	}

	.modal-header {
		display: flex;
		justify-content: flex-end;
		padding: 20px 32px 0;
	}

	.close-button {
		background: #333;
		border: none;
		color: #fff;
		width: 32px;
		height: 32px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		cursor: pointer;
		transition: background-color 0.2s;
	}

	.close-button:hover {
		background: #555;
	}

	.modal-content,
	.verification-content {
		padding: 0 32px 32px 32px;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 24px;
	}

	.verification-content {
		width: 100%;
	}

	.icon-container {
		color: #4a6cf7;
		margin-bottom: 8px;
	}

	.modal-title {
		color: #ffffff;
		font-size: 24px;
		font-weight: 600;
		margin: 0;
		text-align: center;
	}

	.instruction {
		text-align: center;
		color: #ccc;
		line-height: 1.5;
	}

	.instruction p {
		margin: 4px 0;
	}

	.email {
		color: #4a6cf7;
		font-weight: 600;
		font-size: 16px;
	}

	.countdown-text {
		margin-top: 12px;
		color: #888;
		font-size: 14px;
	}

	.countdown-timer {
		color: #4a6cf7;
		font-weight: 600;
		font-size: 16px;
		font-family: monospace;
	}

	.countdown-expired {
		margin-top: 12px;
		color: #ff4444;
		font-size: 14px;
		font-weight: 600;
	}

	.verification-form {
		width: 100%;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 20px;
	}

	.otp-container {
		display: flex;
		gap: 12px;
		justify-content: center;
		margin: 16px 0;
	}

	.otp-input {
		width: 48px;
		height: 56px;
		background: #2a2a2a;
		border: 2px solid #404040;
		border-radius: 8px;
		color: #ffffff;
		font-size: 24px;
		font-weight: 600;
		text-align: center;
		transition: border-color 0.2s;
	}

	.otp-input:focus {
		outline: none;
		border-color: #4a6cf7;
	}

	.otp-input:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.resend-section {
		text-align: center;
		color: #888;
		font-size: 14px;
	}

	.resend-section p {
		margin: 0 0 8px 0;
	}

	.resend-button {
		background: none;
		border: none;
		color: #4a6cf7;
		cursor: pointer;
		font-size: 14px;
		text-decoration: underline;
		display: flex;
		align-items: center;
		gap: 8px;
		margin: 0 auto;
		transition: color 0.2s;
	}

	.resend-button:hover:not(:disabled) {
		color: #3a5ae7;
	}

	.resend-button:disabled {
		color: #666;
		cursor: not-allowed;
		text-decoration: none;
	}

	.action-buttons {
		display: flex;
		gap: 12px;
		width: 100%;
		margin-top: 8px;
	}

	.back-button {
		flex: 1;
		padding: 12px 16px;
		background: #2a2a2a;
		border: 1px solid #404040;
		border-radius: 8px;
		color: #ffffff;
		font-size: 14px;
		font-weight: 500;
		cursor: pointer;
		transition: background-color 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	.back-button:hover {
		background: #3a3a3a;
	}

	.verify-button {
		flex: 2;
		padding: 12px 16px;
		background: #4a6cf7;
		border: none;
		border-radius: 8px;
		color: #ffffff;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: background-color 0.2s;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 8px;
	}

	.verify-button:hover:not(:disabled) {
		background: #3a5ae7;
	}

	.verify-button:disabled {
		opacity: 0.6;
		cursor: not-allowed;
	}

	.error-message {
		color: #ff4444;
		font-size: 14px;
		text-align: center;
		padding: 8px 16px;
		background: rgba(255, 68, 68, 0.1);
		border-radius: 8px;
		margin: 8px 0;
	}

	.success-message {
		color: #22c55e;
		font-size: 14px;
		text-align: center;
		padding: 8px 16px;
		background: rgba(34, 197, 94, 0.1);
		border-radius: 8px;
		margin: 8px 0;
	}

	.spinner {
		width: 16px;
		height: 16px;
		border: 2px solid transparent;
		border-top: 2px solid currentColor;
		border-radius: 50%;
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	/* Mobile responsive */
	@media (max-width: 768px) {
		.modal-container {
			margin: 10px;
			max-width: calc(100% - 20px);
		}

		.modal-content {
			padding: 0 20px 20px 20px;
		}

		.modal-title {
			font-size: 20px;
		}

		.otp-container {
			gap: 8px;
		}

		.otp-input {
			width: 40px;
			height: 48px;
			font-size: 20px;
		}

		.action-buttons {
			flex-direction: column;
		}

		.back-button,
		.verify-button {
			flex: 1;
		}
	}
</style>
